import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="support--file-dropzone"
//
// Drag-and-drop file upload for support tickets/comments/notes. Files
// accumulate across drops/selections (a new selection adds instead of
// replacing), each can be removed individually, and the list clears when the
// surrounding form is reset. Client-side type/size validation mirrors the
// server allow-list (images, PDF, Excel and CSV, 25 MB) for immediate feedback;
// the server stays authoritative via SupportTicketComment / SupportInternalNote
// validations. Macro-enabled Excel files (.xlsm, .xlsb) are not allowed.
const ALLOWED_EXTENSIONS = [
  ".png",
  ".jpg",
  ".jpeg",
  ".gif",
  ".webp",
  ".pdf",
  ".xlsx",
  ".xls",
  ".csv",
]
const ALLOWED_MIME_TYPES = [
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
  "text/csv",
]
const MAX_BYTES = 25 * 1024 * 1024

export default class extends Controller {
  static targets = ["input", "droparea", "filelist", "error"]
  static values = {
    maxBytes: { type: Number, default: MAX_BYTES },
    invalidTypeMessage: {
      type: String,
      default:
        "Only image (.png, .jpg, .gif, .webp), PDF or Excel (.xlsx, .xls, .csv) files are allowed",
    },
    fileTooLargeMessage: {
      type: String,
      default: "File is too large (max 25 MB)",
    },
  }

  connect() {
    this.dataTransfer = new DataTransfer()
    this.boundPreventDefault = this.preventDefault.bind(this)
    ;["dragenter", "dragover", "dragleave", "drop"].forEach((event) => {
      this.dropareaTarget.addEventListener(event, this.boundPreventDefault)
    })

    this.boundClear = this.clear.bind(this)
    this.form = this.element.closest("form")
    if (this.form) this.form.addEventListener("reset", this.boundClear)
  }

  disconnect() {
    ;["dragenter", "dragover", "dragleave", "drop"].forEach((event) => {
      this.dropareaTarget.removeEventListener(event, this.boundPreventDefault)
    })
    if (this.form) this.form.removeEventListener("reset", this.boundClear)
  }

  preventDefault(e) {
    e.preventDefault()
    e.stopPropagation()
  }

  dragenter() {
    this.setHover(true)
  }

  dragover() {
    this.setHover(true)
  }

  dragleave() {
    this.setHover(false)
  }

  drop(e) {
    this.setHover(false)
    this.addFiles(e.dataTransfer.files)
  }

  selectFiles() {
    this.inputTarget.click()
  }

  inputChanged() {
    // The native dialog replaces input.files; merge its selection into the
    // accumulated set, then re-sync the input so nothing is lost.
    this.addFiles(this.inputTarget.files)
  }

  removeFile(index) {
    const kept = Array.from(this.dataTransfer.files).filter(
      (_, i) => i !== index,
    )
    this.dataTransfer = new DataTransfer()
    kept.forEach((f) => this.dataTransfer.items.add(f))
    this.sync()
  }

  clear() {
    this.dataTransfer = new DataTransfer()
    this.showError(null)
    this.sync()
  }

  addFiles(fileList) {
    let rejection = null
    Array.from(fileList).forEach((file) => {
      const validity = this.validate(file)
      if (!validity.ok) {
        if (!rejection) rejection = validity.message
        return
      }
      const duplicate = Array.from(this.dataTransfer.files).some(
        (existing) =>
          existing.name === file.name && existing.size === file.size,
      )
      if (!duplicate) this.dataTransfer.items.add(file)
    })
    this.showError(rejection)
    this.sync()
  }

  validate(file) {
    const ext = file.name.includes(".")
      ? file.name.slice(file.name.lastIndexOf(".")).toLowerCase()
      : ""
    const typeOk =
      ALLOWED_MIME_TYPES.includes(file.type) || ALLOWED_EXTENSIONS.includes(ext)
    if (!typeOk) return { ok: false, message: this.invalidTypeMessageValue }
    if (file.size > this.maxBytesValue) {
      return { ok: false, message: this.fileTooLargeMessageValue }
    }
    return { ok: true }
  }

  showError(message) {
    if (!this.hasErrorTarget) return
    if (!message) {
      this.errorTarget.textContent = ""
      this.errorTarget.classList.add("hidden")
      return
    }
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("hidden")
  }

  sync() {
    this.inputTarget.files = this.dataTransfer.files
    this.renderFileList()
  }

  renderFileList() {
    if (!this.hasFilelistTarget) return

    const files = Array.from(this.dataTransfer.files)
    if (files.length === 0) {
      this.filelistTarget.innerHTML = ""
      return
    }

    const ul = document.createElement("ul")
    ul.className = "mt-2 space-y-1"

    files.forEach((file, index) => {
      const li = document.createElement("li")
      li.className =
        "flex items-center justify-between gap-2 rounded-md border border-border bg-muted/40 px-2.5 py-1.5 text-sm text-foreground"

      const name = document.createElement("span")
      name.className = "truncate"
      name.textContent = `${file.name} (${this.formatSize(file.size)})`

      const remove = document.createElement("button")
      remove.type = "button"
      remove.className = "shrink-0 text-destructive hover:text-destructive/80"
      remove.setAttribute("aria-label", `Remove ${file.name}`)
      remove.textContent = "✕"
      remove.addEventListener("click", () => this.removeFile(index))

      li.appendChild(name)
      li.appendChild(remove)
      ul.appendChild(li)
    })

    this.filelistTarget.replaceChildren(ul)
  }

  setHover(on) {
    this.dropareaTarget.classList.toggle("border-primary-500", on)
    this.dropareaTarget.classList.toggle("bg-primary-50", on)
    this.dropareaTarget.classList.toggle("border-dashed", !on)
  }

  formatSize(bytes) {
    if (bytes === 0) return "0 B"
    const units = ["B", "KB", "MB", "GB"]
    const i = Math.floor(Math.log(bytes) / Math.log(1024))
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`
  }
}
